import { inject } from '@angular/core';
import { HttpEvent, HttpRequest, HttpResponse, HttpErrorResponse, HttpHandlerFn } from '@angular/common/http';
import { catchError, map, retry, timeout } from 'rxjs/operators';
import { EMPTY, Observable, throwError, timer } from 'rxjs';
import { UserService } from '@core/services/user.service';
import {
	RETRY_COUNT,
	RETRY_DELAY_MS,
	TIMEOUT_MS,
	SKIP_ERROR,
	SKIP_RESPONSE_INTERCEPTOR,
	FULL_RESPONSE
} from '@core/tokens/http.token';
import ToastManager from '@core/toast-manager';

export function responseInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
	const toast = inject(ToastManager);
	const userService = inject(UserService);

	const timeOut: number = req.context.get(TIMEOUT_MS);
	const retries: number = req.context.get(RETRY_COUNT);
	const retryDelay: number = req.context.get(RETRY_DELAY_MS);
	const skipError: boolean = req.context.get(SKIP_ERROR);
	const fullResponse: boolean = req.context.get(FULL_RESPONSE);
	const skipResponseInterceptor: boolean = req.context.get(SKIP_RESPONSE_INTERCEPTOR);

	const shouldRetry = (err: HttpErrorResponse, req: HttpRequest<unknown>): boolean => {
		// Retry only idempotent methods & transient errors
		const idempotent = req.method === 'GET' || req.method === 'HEAD';
		const transient = err.status === 0 || (err.status >= 500 && err.status < 600);
		return idempotent && transient;
	};

	return next(req).pipe(
		map((res: HttpEvent<any>) => {
			if (res instanceof HttpResponse) {
				if (res.status >= 200 && res.status < 300) {
					const _body: CustomResponse<any> = res.body || {};
					const data = !skipResponseInterceptor && _body && 'data' in _body ? _body.data : _body;
					if (_body.message) {
						toast
							.dismissible(false)
							.show(_body.message.title, _body.message.description, { status: _body.message.type });
					}
					return res.clone({ body: fullResponse ? res : data });
				}
			}
			return res;
		}),
		// No-op unless a caller opts in with `retryOnError`, which leaves RETRY_COUNT at 0.
		retry({
			count: retries,
			delay: (error: any, retryIndex: number) => {
				if (!shouldRetry(error as HttpErrorResponse, req)) {
					throw error;
				}
				const backoffMs = (retryIndex + 1) * retryDelay;
				return timer(backoffMs);
			},
			resetOnSuccess: true
		}),
		timeout({
			each: timeOut,
			with: () =>
				throwError(() => new HttpErrorResponse({ status: 408, error: 'Request timed out in frontend !' }))
		}),
		catchError((res: HttpErrorResponse) => {
			if (res.status === 440) {
				// logoutRedirect() navigates away from the app, so a toast would never be read
				userService.logout();
			} else if (!skipError) {
				if (res.status === 422) {
					toast.dismissible().danger(res.error?.error || 'Validation Error', res.error?.message);
				} else {
					toast.dismissible().danger(res.error?.error || res.statusText, res.error?.message || res.message);
				}
			}
			return throwError(() => res);
		})
	);
}

interface CustomResponse<T> {
	data: T;
	message?: ResponseMessage;
}
interface ResponseMessage {
	type?: 'info' | 'warning' | 'danger' | 'success' | 'primary' | 'basic';
	title: string;
	description?: string;
}
